import { useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, Phone, Mail, MapPin, MessageCircle, Smartphone, Bell, X } from "lucide-react";
import { toast } from "sonner";
import { useWebConfig } from "@/hooks/useWebConfig";
import { webApi, errMsg } from "@/lib/api";

function WaitlistPopover({ name, platform, dark, onClose }) {
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (mobile.length !== 10) return toast.error("Enter a valid 10-digit mobile number");
    setBusy(true);
    try { const r = await webApi.post("/app-waitlist", { mobile, platform }); setDone(true); toast.success(r.already ? "You're already on the list!" : "We'll notify you when the app launches"); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className={`absolute z-20 left-0 top-full mt-2 w-64 rounded-2xl p-3 shadow-xl border ${dark ? "bg-[var(--brand-deep)] border-white/20 text-white" : "bg-white border-[var(--line)] text-[var(--on-surface)]"}`} data-testid={`waitlist-${platform}`}>
      <div className="flex items-start justify-between gap-2"><p className="text-xs font-semibold flex items-center gap-1"><Bell size={13} /> Notify me when {name} app is live</p><button type="button" onClick={onClose} aria-label="Close" className="opacity-70 hover:opacity-100"><X size={14} /></button></div>
      {done ? <p className="text-xs mt-2 opacity-90" data-testid="waitlist-done">✓ You're on the list. We'll text you at +91 {mobile}.</p> : (
        <div className="flex gap-2 mt-2">
          <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" placeholder="10-digit mobile" className="input !py-1.5 text-sm flex-1 !text-[var(--on-surface)]" data-testid="waitlist-mobile" />
          <button disabled={busy} className="btn-brand text-xs py-1.5 px-3 whitespace-nowrap" data-testid="waitlist-submit">{busy ? "…" : "Notify me"}</button>
        </div>
      )}
    </form>
  );
}

export function StoreBadge({ name, url, dark }) {
  const [open, setOpen] = useState(false);
  const id = `store-${name.replace(" ", "-").toLowerCase()}`;
  const platform = name === "App Store" ? "ios" : "android";
  const base = `relative inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold border transition-colors ${dark ? "border-white/25 text-white" : "border-[var(--line)] text-[var(--on-surface)]"}`;
  if (url) return <a href={url} target="_blank" rel="noreferrer" className={`${base} ${dark ? "hover:bg-white/10" : "hover:bg-[var(--surface3)]"}`} data-testid={id}><Smartphone size={16} /> {name}</a>;
  return (
    <span className="relative inline-block">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`${base} ${dark ? "hover:bg-white/10" : "hover:bg-[var(--surface3)]"}`} title="Tap to get notified at launch" data-testid={id}>
        <Smartphone size={16} /> {name}
        <span className="absolute -top-2.5 -right-2 rounded-full bg-[#f5a623] text-[#1b1b1b] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 shadow" data-testid={`${id}-coming-soon`}>Coming soon</span>
      </button>
      {open && <WaitlistPopover name={name} platform={platform} dark={dark} onClose={() => setOpen(false)} />}
    </span>
  );
}

const COLS = [
  ["Shop", [["Fruits", "/fruits"], ["Vegetables", "/vegetables"], ["Organic Products", "/organic"], ["Weekly Basket", "/weekly-basket"], ["Offers & Coupons", "/offers"]]],
  ["Company", [["About Us", "/about"], ["Contact Us", "/contact"], ["Become an Area Partner", "/area-partner"], ["Partner Login", "/login"]]],
  ["Policies", [["Terms & Conditions", "/terms"], ["Privacy Policy", "/privacy"], ["Refund & Cancellation", "/refund-policy"]]],
];

export function Footer() {
  const cfg = useWebConfig();
  return (
    <footer className="mt-16 bg-[var(--brand-deep)] text-white relative grain" data-testid="site-footer">
      <div className="container-x py-12 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr] relative">
        <div>
          <div className="flex items-center gap-2"><span className="grid place-items-center w-9 h-9 rounded-xl bg-white/15"><Leaf size={20} /></span><span className="font-display text-2xl font-semibold">Jaivik Bazaar</span></div>
          <p className="mt-3 text-sm text-white/75 max-w-xs">Fresh • Natural • Trusted. Farm-fresh fruits, vegetables and baskets delivered by your neighbourhood Area Partner.</p>
          <div className="mt-5 grid gap-2 text-sm text-white/85">
            <a href={`tel:${cfg.phone}`} className="flex items-center gap-2 hover:text-white"><Phone size={15} /> {cfg.phone}</a>
            <a href={`https://wa.me/${cfg.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white"><MessageCircle size={15} /> WhatsApp us</a>
            <a href={`mailto:${cfg.email}`} className="flex items-center gap-2 hover:text-white"><Mail size={15} /> {cfg.email}</a>
            <span className="flex items-center gap-2"><MapPin size={15} /> {cfg.address}</span>
          </div>
          <div className="mt-6" data-testid="footer-app-links">
            <p className="text-xs uppercase tracking-wide text-white/60 mb-3">Get the app</p>
            <div className="flex flex-wrap gap-3 pt-1"><StoreBadge name="Google Play" url={cfg.play_store} dark /><StoreBadge name="App Store" url={cfg.app_store} dark /></div>
          </div>
        </div>
        {COLS.map(([h, links]) => (
          <div key={h}>
            <h3 className="font-display font-semibold text-lg mb-3">{h}</h3>
            <ul className="grid gap-2 text-sm text-white/80">{links.map(([l, to]) => <li key={to}><Link to={to} className="hover:text-white hover:underline underline-offset-4">{l}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10"><div className="container-x py-4 text-xs text-white/60 flex flex-col sm:flex-row gap-2 justify-between"><span>© {new Date().getFullYear()} Jaivik Bazaar. All rights reserved.</span><span>jaivikbazaar.com</span></div></div>
    </footer>
  );
}
